import { useMemo, useState } from "react";
import { Card } from "antd";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import Footer from "../components/Footer";
import Header from "../components/Header";
import StandardButton from "../components/StandardButton";

const DEFAULT_JSON = `{
  "store": {
    "book": [
      { "category": "reference", "author": "Nigel Rees", "title": "Sayings of the Century", "price": 8.95 },
      { "category": "fiction", "author": "Evelyn Waugh", "title": "Sword of Honour", "price": 12.99 },
      { "category": "fiction", "author": "Herman Melville", "title": "Moby Dick", "isbn": "0-553-21311-3", "price": 8.99 },
      { "category": "fiction", "author": "J. R. R. Tolkien", "title": "The Lord of the Rings", "isbn": "0-395-19395-8", "price": 22.99 }
    ],
    "bicycle": { "color": "red", "price": 19.95 }
  },
  "expensive": 10
}`;

const EXAMPLES = [
  { key: "all_books", path: "$.store.book[*]" },
  { key: "all_authors", path: "$.store.book[*].author" },
  { key: "all_titles", path: "$..title" },
  { key: "first_book", path: "$.store.book[0]" },
  { key: "last_book", path: "$.store.book[-1]" },
  { key: "first_two_books", path: "$.store.book[0:2]" },
  { key: "all_prices", path: "$..price" },
  { key: "all_isbns", path: "$..isbn" },
  { key: "everything", path: "$..*" },
];

const tokenize = (path) => {
  const tokens = [];
  let i = 0;
  const len = path.length;

  if (len === 0 || (path[0] !== "$" && path[0] !== "@")) {
    throw new Error("path_must_start_with_root");
  }
  tokens.push({ type: "root" });
  i += 1;

  while (i < len) {
    const ch = path[i];

    if (ch === ".") {
      if (path[i + 1] === ".") {
        tokens.push({ type: "descend" });
        i += 2;
        if (i >= len) throw new Error("trailing_descend");
        if (path[i] === "*") {
          tokens.push({ type: "wildcard" });
          i += 1;
        } else if (/[A-Za-z_$]/.test(path[i])) {
          let name = "";
          while (i < len && /[A-Za-z0-9_\-$]/.test(path[i])) {
            name += path[i];
            i += 1;
          }
          tokens.push({ type: "name", value: name });
        } else if (path[i] !== "[") {
          throw new Error("unsupported_descend_target");
        }
        continue;
      }
      i += 1;
      if (i >= len) throw new Error("trailing_dot");
      if (path[i] === "*") {
        tokens.push({ type: "wildcard" });
        i += 1;
        continue;
      }
      let name = "";
      while (i < len && /[A-Za-z0-9_\-$]/.test(path[i])) {
        name += path[i];
        i += 1;
      }
      if (!name) throw new Error("expected_property_after_dot");
      tokens.push({ type: "name", value: name });
      continue;
    }

    if (ch === "[") {
      const close = path.indexOf("]", i);
      if (close === -1) throw new Error("unclosed_bracket");
      const inner = path.slice(i + 1, close).trim();
      if (!inner) throw new Error("empty_bracket");

      if (inner === "*") {
        tokens.push({ type: "wildcard" });
      } else if (
        (inner.startsWith("'") && inner.endsWith("'")) ||
        (inner.startsWith('"') && inner.endsWith('"'))
      ) {
        tokens.push({ type: "name", value: inner.slice(1, -1) });
      } else if (inner.includes(":")) {
        const parts = inner.split(":");
        if (parts.length > 3) throw new Error("invalid_slice");
        const [s, e, st] = parts.map((p) => p.trim());
        const start = s === "" ? null : parseInt(s, 10);
        const end = e === "" ? null : parseInt(e, 10);
        const step = st === undefined || st === "" ? null : parseInt(st, 10);
        if (
          (s !== "" && Number.isNaN(start)) ||
          (e !== "" && Number.isNaN(end)) ||
          (st !== undefined && st !== "" && Number.isNaN(step))
        ) {
          throw new Error("invalid_slice");
        }
        tokens.push({ type: "slice", start, end, step });
      } else if (inner.includes(",")) {
        const parts = inner.split(",").map((p) => p.trim());
        const indices = [];
        const names = [];
        for (const p of parts) {
          if (
            (p.startsWith("'") && p.endsWith("'")) ||
            (p.startsWith('"') && p.endsWith('"'))
          ) {
            names.push(p.slice(1, -1));
          } else {
            const n = parseInt(p, 10);
            if (Number.isNaN(n)) throw new Error("invalid_union_element");
            indices.push(n);
          }
        }
        tokens.push({ type: "union", indices, names });
      } else {
        const n = parseInt(inner, 10);
        if (Number.isNaN(n)) throw new Error("invalid_bracket_content");
        tokens.push({ type: "index", value: n });
      }
      i = close + 1;
      continue;
    }

    throw new Error("unexpected_character");
  }

  return tokens;
};

const isObject = (v) => v !== null && typeof v === "object" && !Array.isArray(v);

const collectAllDescendants = (value, out) => {
  if (Array.isArray(value)) {
    for (const item of value) {
      out.push(item);
      collectAllDescendants(item, out);
    }
  } else if (isObject(value)) {
    for (const key of Object.keys(value)) {
      out.push(value[key]);
      collectAllDescendants(value[key], out);
    }
  }
};

const collectDescendantsWithKey = (value, key, out) => {
  if (Array.isArray(value)) {
    for (const item of value) {
      collectDescendantsWithKey(item, key, out);
    }
  } else if (isObject(value)) {
    if (Object.prototype.hasOwnProperty.call(value, key)) {
      out.push(value[key]);
    }
    for (const k of Object.keys(value)) {
      collectDescendantsWithKey(value[k], key, out);
    }
  }
};

const collectDescendantsWithIndex = (value, idx, out) => {
  if (Array.isArray(value)) {
    const real = idx < 0 ? value.length + idx : idx;
    if (real >= 0 && real < value.length) out.push(value[real]);
    for (const item of value) {
      collectDescendantsWithIndex(item, idx, out);
    }
  } else if (isObject(value)) {
    for (const k of Object.keys(value)) {
      collectDescendantsWithIndex(value[k], idx, out);
    }
  }
};

const evalTokens = (root, tokens) => {
  let current = [root];

  for (let ti = 0; ti < tokens.length; ti += 1) {
    const tok = tokens[ti];

    if (tok.type === "root") {
      current = [root];
      continue;
    }

    if (tok.type === "descend") {
      const next = tokens[ti + 1];
      if (!next) throw new Error("trailing_descend");
      const out = [];
      if (next.type === "name") {
        for (const v of current) collectDescendantsWithKey(v, next.value, out);
      } else if (next.type === "wildcard") {
        for (const v of current) {
          if (Array.isArray(v)) for (const item of v) out.push(item);
          else if (isObject(v)) for (const k of Object.keys(v)) out.push(v[k]);
          collectAllDescendants(v, out);
        }
      } else if (next.type === "index") {
        for (const v of current) collectDescendantsWithIndex(v, next.value, out);
      } else {
        throw new Error("unsupported_descend_target");
      }
      current = out;
      ti += 1;
      continue;
    }

    const next = [];

    for (const v of current) {
      if (tok.type === "name") {
        if (isObject(v) && Object.prototype.hasOwnProperty.call(v, tok.value)) {
          next.push(v[tok.value]);
        }
      } else if (tok.type === "wildcard") {
        if (Array.isArray(v)) for (const item of v) next.push(item);
        else if (isObject(v)) for (const k of Object.keys(v)) next.push(v[k]);
      } else if (tok.type === "index") {
        if (Array.isArray(v)) {
          const idx = tok.value < 0 ? v.length + tok.value : tok.value;
          if (idx >= 0 && idx < v.length) next.push(v[idx]);
        }
      } else if (tok.type === "slice") {
        if (Array.isArray(v)) {
          const step = tok.step ?? 1;
          if (step === 0) throw new Error("invalid_slice");
          const length = v.length;
          let start = tok.start;
          let end = tok.end;
          if (start === null || start === undefined) start = step > 0 ? 0 : length - 1;
          else if (start < 0) start = Math.max(length + start, step > 0 ? 0 : -1);
          else start = Math.min(start, length);
          if (end === null || end === undefined) end = step > 0 ? length : -1;
          else if (end < 0) end = Math.max(length + end, -1);
          else end = Math.min(end, length);
          if (step > 0) {
            for (let i = start; i < end; i += step) next.push(v[i]);
          } else {
            for (let i = start; i > end; i += step) next.push(v[i]);
          }
        }
      } else if (tok.type === "union") {
        if (Array.isArray(v)) {
          for (const idx of tok.indices) {
            const real = idx < 0 ? v.length + idx : idx;
            if (real >= 0 && real < v.length) next.push(v[real]);
          }
        }
        if (isObject(v)) {
          for (const k of tok.names) {
            if (Object.prototype.hasOwnProperty.call(v, k)) next.push(v[k]);
          }
        }
      }
    }
    current = next;
  }

  return current;
};

const evaluatePath = (root, path) => {
  const tokens = tokenize(path.trim());
  return evalTokens(root, tokens);
};

const JsonPathTester = () => {
  const { t } = useTranslation();
  const [jsonText, setJsonText] = useState(DEFAULT_JSON);
  const [path, setPath] = useState("$.store.book[*].author");
  const [outputAsArray, setOutputAsArray] = useState(true);

  const parsed = useMemo(() => {
    const trimmed = jsonText.trim();
    if (!trimmed) return { ok: false, errorKey: "json_path_error_empty_json" };
    try {
      return { ok: true, value: JSON.parse(trimmed) };
    } catch (err) {
      return {
        ok: false,
        errorKey: "json_path_error_invalid_json",
        detail: err.message,
      };
    }
  }, [jsonText]);

  const result = useMemo(() => {
    if (!parsed.ok) return parsed;
    const trimmedPath = path.trim();
    if (!trimmedPath) {
      return { ok: false, errorKey: "json_path_error_empty_path" };
    }
    try {
      const matches = evaluatePath(parsed.value, trimmedPath);
      return { ok: true, matches };
    } catch (err) {
      return {
        ok: false,
        errorKey: `json_path_error_${err.message}`,
        fallbackKey: "json_path_error_invalid_path",
      };
    }
  }, [parsed, path]);

  const output = useMemo(() => {
    if (!result.ok) return "";
    if (result.matches.length === 0) return "";
    if (!outputAsArray && result.matches.length === 1) {
      return JSON.stringify(result.matches[0], null, 2);
    }
    return JSON.stringify(result.matches, null, 2);
  }, [result, outputAsArray]);

  const copy = async () => {
    if (!output) {
      toast.error(t("json_path_nothing_to_copy"));
      return;
    }
    try {
      await navigator.clipboard.writeText(output);
      toast.success(t("copy_success"));
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const loadExample = (p) => setPath(p);
  const clearJson = () => setJsonText("");

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-4">
            <h1 className="text-2xl font-bold text-indigo-700">
              {t("json_path_title")}
            </h1>
            <p className="text-gray-600">{t("json_path_intro")}</p>

            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700" htmlFor="jpt-json-input">
                  {t("json_path_json_label")}
                </label>
                <StandardButton
                  onClick={clearJson}
                  type="secondary"
                  variant="outlined"
                  size="small"
                  disabled={!jsonText}
                >
                  {t("json_path_clear")}
                </StandardButton>
              </div>
              <textarea
                id="jpt-json-input"
                value={jsonText}
                onChange={(e) => setJsonText(e.target.value)}
                placeholder={t("json_path_json_placeholder")}
                rows={10}
                spellCheck={false}
                aria-label={t("json_path_json_label")}
                className="w-full p-2 font-mono text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none resize-y"
              />
              {!parsed.ok && jsonText.trim() && (
                <p className="text-sm text-red-600 break-all" role="alert">
                  {t(parsed.errorKey)}
                  {parsed.detail ? ` — ${parsed.detail}` : ""}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700" htmlFor="jpt-path-input">
                {t("json_path_query_label")}
              </label>
              <input
                id="jpt-path-input"
                type="text"
                value={path}
                onChange={(e) => setPath(e.target.value)}
                placeholder={t("json_path_query_placeholder")}
                spellCheck={false}
                aria-label={t("json_path_query_label")}
                className="w-full p-2 font-mono text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none"
              />
            </div>

            <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200 flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-gray-700">
                  {t("json_path_examples_label")}
                </label>
                <div className="flex flex-wrap gap-2">
                  {EXAMPLES.map((ex) => (
                    <StandardButton
                      key={ex.key}
                      onClick={() => loadExample(ex.path)}
                      type={path.trim() === ex.path ? "primary" : "secondary"}
                      variant={path.trim() === ex.path ? undefined : "outlined"}
                      size="small"
                    >
                      <span className="font-mono mr-1">{ex.path}</span>
                      <span className="text-xs opacity-80">
                        — {t(`json_path_example_${ex.key}`)}
                      </span>
                    </StandardButton>
                  ))}
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={outputAsArray}
                  onChange={(e) => setOutputAsArray(e.target.checked)}
                  className="h-4 w-4"
                />
                {t("json_path_always_array")}
              </label>
            </div>

            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">
                  {t("json_path_output_label")}
                </label>
                <StandardButton
                  onClick={copy}
                  type="primary"
                  size="small"
                  disabled={!output}
                >
                  {t("copy_button")}
                </StandardButton>
              </div>
              {result.ok ? (
                <>
                  <pre className="w-full min-h-[6rem] max-h-[24rem] overflow-auto p-2 font-mono text-sm bg-gray-50 border border-gray-200 rounded-md whitespace-pre text-gray-800">
                    {output || (
                      <em className="text-gray-400 not-italic">
                        {t("json_path_output_empty")}
                      </em>
                    )}
                  </pre>
                  <div className="text-xs text-gray-500">
                    {t("json_path_match_count", { count: result.matches.length })}
                  </div>
                </>
              ) : (
                <p className="text-sm text-red-600 break-all" role="alert">
                  {t(result.errorKey, t(result.fallbackKey || result.errorKey))}
                </p>
              )}
            </div>

            <div className="text-xs text-gray-500 leading-relaxed">
              <p>{t("json_path_note_syntax")}</p>
              <p>{t("json_path_note_filters")}</p>
              <p>{t("json_path_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default JsonPathTester;
